import { useCallback, useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { musicService } from "@/services/musicService";
import type { Playlist } from "@/types/types";

interface P {
  open: boolean;
  onClose: () => void;
  trackId: string | null;
  /** Optional callback fired after a successful add. */
  onAdded?: (playlistId: string) => void;
}

export function AddToPlaylistModal({ open, onClose, trackId, onAdded }: P) {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<Set<string>>(new Set());
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { const p = await musicService.playlists(); setPlaylists(p.items); }
    catch (e) { console.error(e); setError("Impossible de charger les playlists"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open) { setJustAdded(new Set()); setError(null); return; }
    void refresh();
  }, [open, refresh]);

  const add = useCallback(async (playlistId: string) => {
    if (!trackId) return;
    setAddingTo(playlistId); setError(null);
    try {
      await musicService.addTracksToPlaylist(playlistId, [trackId]);
      setJustAdded((s) => { const n = new Set(s); n.add(playlistId); return n; });
      onAdded?.(playlistId);
    } catch (e) {
      console.error(e); setError("Ajout échoué");
    } finally { setAddingTo(null); }
  }, [trackId, onAdded]);

  const createAndAdd = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name || !trackId) return;
    setAddingTo("__new__"); setError(null);
    try {
      const created = await musicService.createPlaylist(name, false);
      if (created?.id) {
        await musicService.addTracksToPlaylist(created.id, [trackId]);
        setJustAdded((s) => { const n = new Set(s); n.add(created.id); return n; });
        onAdded?.(created.id);
        setNewName("");
        await refresh();
      }
    } catch (err) {
      console.error(err); setError("Création impossible");
    } finally { setAddingTo(null); }
  }, [newName, trackId, onAdded, refresh]);

  return (
    <Modal open={open} onClose={onClose} title="Ajouter à une playlist">
      <form onSubmit={createAndAdd}
            style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Créer une nouvelle playlist..."
          maxLength={80}
          style={{
            flex: 1, background: "var(--bg-elev-2)", border: "1px solid var(--line)",
            color: "var(--fg)", padding: "9px 12px", borderRadius: 8, fontSize: 14,
          }}
        />
        <button type="submit" className="btn primary"
                disabled={!newName.trim() || addingTo === "__new__"}>
          {addingTo === "__new__" ? "…" : "Créer"}
        </button>
      </form>

      <div style={{ maxHeight: "50vh", overflowY: "auto", margin: "0 -8px" }}>
        {loading && <div className="muted" style={{ padding: 12 }}>Chargement…</div>}
        {!loading && playlists.length === 0 && (
          <div className="muted" style={{ padding: 12 }}>
            Pas encore de playlist. Utilise le champ au-dessus pour en créer une.
          </div>
        )}
        {playlists.map((p) => {
          const added = justAdded.has(p.id);
          const busy = addingTo === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => void add(p.id)}
              disabled={busy || added}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", padding: "10px 12px", borderRadius: 8,
                background: added ? "rgba(139,92,246,.12)" : "transparent",
                border: "1px solid transparent",
                color: "var(--fg)", textAlign: "left", cursor: added ? "default" : "pointer",
                transition: "background .12s",
              }}
              onMouseEnter={(e) => { if (!added) e.currentTarget.style.background = "var(--bg-elev-2)"; }}
              onMouseLeave={(e) => { if (!added) e.currentTarget.style.background = "transparent"; }}
            >
              <span className="trunc" style={{ maxWidth: "70%" }}>{p.name}</span>
              <span className="muted" style={{ fontSize: 13 }}>
                {added ? "✓ ajouté" : busy ? "…" : p.is_public ? "publique" : "privée"}
              </span>
            </button>
          );
        })}
      </div>

      {error && <div style={{ color: "#f87171", fontSize: 13, marginTop: 10 }}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
        <button className="btn" onClick={onClose}>Fermer</button>
      </div>
    </Modal>
  );
}
